import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Upload, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle, ArrowRight, RotateCcw, History, Search, X, UserX, Lock } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import DashboardLayout from '../components/layout/DashboardLayout';
import api from '../lib/api';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function StepIndicator({ currentStep }) {
  function getClass(sNum) {
    if (currentStep === sNum) return 'bg-slate-900 text-white dark:bg-white dark:text-slate-900';
    if (currentStep > sNum) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
    return 'bg-muted text-muted-foreground';
  }

  return (
    <div className="flex items-center gap-2 text-sm flex-wrap">
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${getClass(1)}`}>
        {currentStep > 1 ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span>1</span>}
        <span className="hidden sm:inline">Select Month</span>
      </div>
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40" />
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${getClass(2)}`}>
        {currentStep > 2 ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span>2</span>}
        <span className="hidden sm:inline">Upload</span>
      </div>
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40" />
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${getClass(3)}`}>
        {currentStep > 3 ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span>3</span>}
        <span className="hidden sm:inline">Map Columns</span>
      </div>
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40" />
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${getClass(4)}`}>
        {currentStep > 4 ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span>4</span>}
        <span className="hidden sm:inline">Processing</span>
      </div>
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40" />
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${getClass(5)}`}>
        {currentStep > 5 ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span>5</span>}
        <span className="hidden sm:inline">Preview</span>
      </div>
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40" />
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${getClass(6)}`}>
        {currentStep > 6 ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span>6</span>}
        <span className="hidden sm:inline">Done</span>
      </div>
    </div>
  );
}

function PreviewRow({ row, idx }) {
  if (row.missing) {
    return (
      <TableRow className="bg-orange-50/50 dark:bg-orange-950/10" data-testid={`missing-row-${row.keka_emp_number}`}>
        <TableCell className="font-mono text-sm">{row.keka_emp_number}</TableCell>
        <TableCell className="text-muted-foreground italic">Not in Excel</TableCell>
        <TableCell><span className="text-orange-700 dark:text-orange-400">{row.paytrack_name}</span></TableCell>
        <TableCell className="text-sm text-muted-foreground">{row.organization || '-'}</TableCell>
        <TableCell className="text-center text-muted-foreground">—</TableCell>
        <TableCell className="text-center">
          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 text-xs">
            <UserX className="h-3 w-3 mr-1" />Missing from Excel
          </Badge>
        </TableCell>
      </TableRow>
    );
  }
  const bgClass = !row.matched
    ? row.reject_reason === 'Exited' ? 'bg-orange-50/50 dark:bg-orange-950/10' : 'bg-red-50/50 dark:bg-red-950/10'
    : row.finalized ? 'bg-sky-50/50 dark:bg-sky-950/10'
    : row.days_absent > 0 ? 'bg-amber-50/50 dark:bg-amber-950/10' : '';
  return (
    <TableRow className={bgClass} data-testid={`preview-row-${row.keka_emp_number}`}>
      <TableCell className="font-mono text-sm">{row.keka_emp_number}</TableCell>
      <TableCell>{row.keka_emp_name}</TableCell>
      <TableCell>
        {row.matched ? (
          <span className="text-emerald-700 dark:text-emerald-400">{row.paytrack_name}</span>
        ) : row.reject_reason === 'Exited' ? (
          <span className="text-orange-600">{row.paytrack_name}</span>
        ) : (
          <span className="text-red-500 italic">Not found</span>
        )}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{row.organization || '-'}</TableCell>
      <TableCell className="text-center">
        <span className={`font-mono font-semibold ${row.days_absent > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
          {row.days_absent}
        </span>
      </TableCell>
      <TableCell className="text-center">
        {row.matched && row.finalized ? (
          <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-300 text-xs">
            <Lock className="h-3 w-3 mr-1" />Finalized — Skipped
          </Badge>
        ) : row.matched ? (
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 text-xs">
            <CheckCircle2 className="h-3 w-3 mr-1" />Matched
          </Badge>
        ) : row.reject_reason === 'Exited' ? (
          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 text-xs">
            <AlertTriangle className="h-3 w-3 mr-1" />Exited
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300 text-xs">
            <XCircle className="h-3 w-3 mr-1" />No Match
          </Badge>
        )}
      </TableCell>
    </TableRow>
  );
}

function PreviewTable({ rows }) {
  const elements = [];
  for (let i = 0; i < rows.length; i++) {
    elements.push(<PreviewRow key={i} row={rows[i]} idx={i} />);
  }
  return <>{elements}</>;
}

function MissingNamesList({ rows }) {
  const items = [];
  for (let i = 0; i < rows.length; i++) {
    const m = rows[i];
    items.push(
      <div key={i} className="flex items-center justify-between text-xs px-2 py-1 border rounded">
        <span><span className="font-mono text-muted-foreground mr-2">{m.keka_emp_number}</span>{m.paytrack_name}</span>
        <span className="text-muted-foreground truncate ml-2">{m.organization || ''}</span>
      </div>
    );
  }
  return <>{items}</>;
}

function HistoryReportRow({ row }) {
  const isMissing = row.status === 'Missing from Excel';
  const isFinalized = row.status === 'Skipped (Finalized)';
  const statusClass = row.status === 'Matched'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
    : isFinalized
    ? 'bg-sky-50 text-sky-700 border-sky-300'
    : row.status === 'Exited' || isMissing
    ? 'bg-orange-50 text-orange-700 border-orange-300'
    : 'bg-red-50 text-red-700 border-red-300';
  const icon = row.status === 'Matched' ? <CheckCircle2 className="h-3 w-3 mr-1" />
    : isFinalized ? <Lock className="h-3 w-3 mr-1" />
    : isMissing ? <UserX className="h-3 w-3 mr-1" />
    : row.status === 'Exited' ? <AlertTriangle className="h-3 w-3 mr-1" />
    : <XCircle className="h-3 w-3 mr-1" />;
  return (
    <TableRow data-testid={`history-row-${row.keka_emp_number}`}>
      <TableCell className="font-mono text-sm">{row.keka_emp_number}</TableCell>
      <TableCell>{isMissing ? <span className="text-muted-foreground italic">Not in Excel</span> : row.keka_emp_name}</TableCell>
      <TableCell>{row.paytrack_name || '-'}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{row.organization || '-'}</TableCell>
      <TableCell className="text-center">
        {isMissing ? <span className="text-muted-foreground">—</span> : <span className={`font-mono font-semibold ${row.days_absent > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>{row.days_absent}</span>}
      </TableCell>
      <TableCell className="text-center">
        <Badge variant="outline" className={`text-xs ${statusClass}`}>{icon}{row.status}</Badge>
      </TableCell>
    </TableRow>
  );
}

function HistoryRows({ rows }) {
  const elements = [];
  for (let i = 0; i < rows.length; i++) {
    elements.push(<HistoryReportRow key={i} row={rows[i]} />);
  }
  return <>{elements}</>;
}

function UploadHistory() {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDetail, setReportDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterOrg, setFilterOrg] = useState('');
  const [filterEmpId, setFilterEmpId] = useState('');

  useEffect(() => {
    api.get('/keka-reports').then(res => {
      setReports(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const loadDetail = async (report) => {
    setSelectedReport(report);
    setDetailLoading(true);
    setFilterStatus('all');
    setFilterOrg('');
    setFilterEmpId('');
    try {
      const res = await api.get('/keka-reports/' + report.month + '/' + report.year);
      setReportDetail(res.data);
    } catch (e) {
      toast.error('Failed to load report');
    } finally {
      setDetailLoading(false);
    }
  };

  const filteredRows = reportDetail ? reportDetail.rows.filter(r => {
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (filterOrg && r.organization && !r.organization.toLowerCase().includes(filterOrg.toLowerCase())) return false;
    if (filterEmpId && !r.keka_emp_number.includes(filterEmpId)) return false;
    return true;
  }) : [];

  // Get unique orgs for the dropdown
  const orgSet = new Set();
  if (reportDetail) {
    for (let i = 0; i < reportDetail.rows.length; i++) {
      if (reportDetail.rows[i].organization) orgSet.add(reportDetail.rows[i].organization);
    }
  }
  const orgList = Array.from(orgSet).sort();

  if (loading) return <Card><CardContent className="p-8 text-center text-muted-foreground">Loading reports...</CardContent></Card>;

  if (reports.length === 0) {
    return (
      <Card><CardContent className="p-12 text-center">
        <History className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
        <p className="font-medium">No upload history yet</p>
        <p className="text-sm text-muted-foreground mt-1">Reports will appear here after you process a Keka Excel upload.</p>
      </CardContent></Card>
    );
  }

  if (!selectedReport) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-lg">Upload History</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead>File</TableHead>
                <TableHead>Uploaded By</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-center">Matched</TableHead>
                <TableHead className="text-center">Missing</TableHead>
                <TableHead className="text-center">Total Rows</TableHead>
                <TableHead className="text-center">Created</TableHead>
                <TableHead className="text-center">Updated</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.map((r, i) => (
                <TableRow key={i} className="cursor-pointer hover:bg-muted/50" onClick={() => loadDetail(r)} data-testid={`report-row-${r.month}-${r.year}`}>
                  <TableCell className="font-medium">{MONTHS[r.month - 1]} {r.year}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.file_name}</TableCell>
                  <TableCell className="text-sm">{r.uploaded_by}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.uploaded_at ? new Date(r.uploaded_at).toLocaleDateString() : '-'}</TableCell>
                  <TableCell className="text-center font-mono text-emerald-600">{r.matched_count}</TableCell>
                  <TableCell className="text-center font-mono text-orange-600">{r.missing_count || 0}</TableCell>
                  <TableCell className="text-center font-mono">{r.total_rows}</TableCell>
                  <TableCell className="text-center font-mono">{r.created || 0}</TableCell>
                  <TableCell className="text-center font-mono">{r.updated || 0}</TableCell>
                  <TableCell><Button variant="ghost" size="sm">View</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  // Detail view
  const matchCount = reportDetail ? reportDetail.rows.filter(r => r.status === 'Matched').length : 0;
  const exitedCount = reportDetail ? reportDetail.rows.filter(r => r.status === 'Exited').length : 0;
  const noMatchCount = reportDetail ? reportDetail.rows.filter(r => r.status === 'No Match').length : 0;
  const histMissingCount = reportDetail ? reportDetail.rows.filter(r => r.status === 'Missing from Excel').length : 0;
  const histFinalizedCount = reportDetail ? reportDetail.rows.filter(r => r.status === 'Skipped (Finalized)').length : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => { setSelectedReport(null); setReportDetail(null); }} data-testid="back-to-list">
          <ArrowRight className="h-3.5 w-3.5 mr-1 rotate-180" />Back
        </Button>
        <h2 className="text-lg font-semibold">{MONTHS[selectedReport.month - 1]} {selectedReport.year}</h2>
        <Badge variant="outline" className="text-xs">{selectedReport.file_name}</Badge>
        <span className="text-xs text-muted-foreground ml-auto">Uploaded by {selectedReport.uploaded_by} on {selectedReport.uploaded_at ? new Date(selectedReport.uploaded_at).toLocaleDateString() : '-'}</span>
      </div>

      {detailLoading ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground">Loading...</CardContent></Card>
      ) : reportDetail && (
        <>
          {/* KPI summary */}
          <div className="grid grid-cols-5 gap-3">
            <Card className={`cursor-pointer transition-all hover:shadow-md ${filterStatus === 'Matched' ? 'ring-2 ring-emerald-500' : ''}`} onClick={() => setFilterStatus(f => f === 'Matched' ? 'all' : 'Matched')}>
              <CardContent className="p-3 text-center"><p className="text-xl font-bold text-emerald-600 font-mono">{matchCount}</p><p className="text-xs text-muted-foreground">Matched</p></CardContent>
            </Card>
            <Card className={`cursor-pointer transition-all hover:shadow-md ${filterStatus === 'Skipped (Finalized)' ? 'ring-2 ring-sky-500' : ''}`} onClick={() => setFilterStatus(f => f === 'Skipped (Finalized)' ? 'all' : 'Skipped (Finalized)')} data-testid="history-filter-finalized">
              <CardContent className="p-3 text-center"><p className="text-xl font-bold text-sky-600 font-mono">{histFinalizedCount}</p><p className="text-xs text-muted-foreground">Finalized (Skipped)</p></CardContent>
            </Card>
            <Card className={`cursor-pointer transition-all hover:shadow-md ${filterStatus === 'Exited' ? 'ring-2 ring-orange-500' : ''}`} onClick={() => setFilterStatus(f => f === 'Exited' ? 'all' : 'Exited')}>
              <CardContent className="p-3 text-center"><p className="text-xl font-bold text-orange-600 font-mono">{exitedCount}</p><p className="text-xs text-muted-foreground">Exited</p></CardContent>
            </Card>
            <Card className={`cursor-pointer transition-all hover:shadow-md ${filterStatus === 'No Match' ? 'ring-2 ring-red-500' : ''}`} onClick={() => setFilterStatus(f => f === 'No Match' ? 'all' : 'No Match')}>
              <CardContent className="p-3 text-center"><p className="text-xl font-bold text-red-600 font-mono">{noMatchCount}</p><p className="text-xs text-muted-foreground">No Match</p></CardContent>
            </Card>
            <Card className={`cursor-pointer transition-all hover:shadow-md ${filterStatus === 'Missing from Excel' ? 'ring-2 ring-orange-500' : ''}`} onClick={() => setFilterStatus(f => f === 'Missing from Excel' ? 'all' : 'Missing from Excel')} data-testid="history-filter-missing">
              <CardContent className="p-3 text-center"><p className="text-xl font-bold text-orange-600 font-mono">{histMissingCount}</p><p className="text-xs text-muted-foreground">Missing from Excel</p></CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Employee ID..." value={filterEmpId} onChange={e => setFilterEmpId(e.target.value)} className="pl-8 w-40 h-9" data-testid="filter-empid" />
            </div>
            <Select value={filterOrg || '__all__'} onValueChange={v => setFilterOrg(v === '__all__' ? '' : v)}>
              <SelectTrigger className="w-56 h-9" data-testid="filter-org"><SelectValue placeholder="All Organizations" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Organizations</SelectItem>
                {orgList.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
            {(filterStatus !== 'all' || filterOrg || filterEmpId) && (
              <Button variant="ghost" size="sm" onClick={() => { setFilterStatus('all'); setFilterOrg(''); setFilterEmpId(''); }} className="text-xs">
                <X className="h-3.5 w-3.5 mr-1" />Clear filters
              </Button>
            )}
            <span className="text-xs text-muted-foreground ml-auto">Showing {filteredRows.length} of {reportDetail.rows.length}</span>
          </div>

          {/* Data table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Emp No.</TableHead>
                      <TableHead>Name (Keka)</TableHead>
                      <TableHead>Name (PayTrack)</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead className="text-center">LOP Days</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <HistoryRows rows={filteredRows} />
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export default function KekaUploadPage() {
  const [activeTab, setActiveTab] = useState('upload');
  const [step, setStep] = useState(1); // 1=month, 2=upload, 3=map columns, 4=processing, 5=preview, 6=done
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [fileName, setFileName] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const fileInputRef = React.useRef(null);
  // Column mapping state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [colEmpNo, setColEmpNo] = useState('');
  const [colName, setColName] = useState('');
  const [colAbsent, setColAbsent] = useState('');
  const [headerLoading, setHeaderLoading] = useState(false);

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an .xlsx or .xls file');
      return;
    }
    setFileName(file.name);
    setUploadedFile(file);
    setHeaderLoading(true);
    setStep(3);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/salary-records/keka-headers', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setExcelHeaders(response.data.headers);
      setColEmpNo(response.data.suggested_empno_idx !== null ? String(response.data.suggested_empno_idx) : '');
      setColName(response.data.suggested_name_idx !== null ? String(response.data.suggested_name_idx) : '');
      setColAbsent(response.data.suggested_absent_idx !== null ? String(response.data.suggested_absent_idx) : '');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to read Excel file');
      setStep(2);
    } finally {
      setHeaderLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleColumnMappingConfirm = async () => {
    if (!colEmpNo || !colAbsent) {
      toast.error('Please select Employee ID and LOP Days columns');
      return;
    }
    if (!uploadedFile) {
      toast.error('File not found. Please re-upload.');
      setStep(2);
      return;
    }
    setIsUploading(true);
    setStep(4);
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('month', selectedMonth);
      formData.append('year', selectedYear);
      formData.append('empno_col', colEmpNo);
      if (colName && colName !== '__none__') formData.append('name_col', colName);
      formData.append('absent_col', colAbsent);
      const response = await api.post('/salary-records/keka-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreviewData(response.data);
      setStep(5);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process Excel file');
      setStep(3);
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmApply = () => {
    if (!previewData) return;
    const hasMissing = (previewData.missing_from_excel || []).length > 0;
    const hasFinalized = (previewData.finalized_count || 0) > 0;
    if (hasMissing || hasFinalized) {
      setConfirmOpen(true);
      return;
    }
    doApply();
  };

  const doApply = async () => {
    setConfirmOpen(false);
    if (!previewData) return;
    const matchedEmployees = [];
    for (let i = 0; i < previewData.preview.length; i++) {
      const r = previewData.preview[i];
      if (r.matched) {
        matchedEmployees.push({ paytrack_emp_id: r.paytrack_emp_id, days_absent: r.days_absent });
      }
    }
    if (matchedEmployees.length === 0) {
      toast.error('No matched employees to update');
      return;
    }
    setIsApplying(true);
    try {
      // Build all_rows for report storage
      const allRows = [];
      for (let i = 0; i < previewData.preview.length; i++) {
        const r = previewData.preview[i];
        allRows.push({
          keka_emp_number: r.keka_emp_number,
          keka_emp_name: r.keka_emp_name,
          days_absent: r.days_absent,
          matched: r.matched,
          paytrack_name: r.paytrack_name,
          paytrack_emp_id: r.paytrack_emp_id,
          organization: r.organization,
          reject_reason: r.reject_reason || null,
          status: r.matched ? (r.finalized ? 'Skipped (Finalized)' : 'Matched') : r.reject_reason === 'Exited' ? 'Exited' : 'No Match',
        });
      }
      const missing = previewData.missing_from_excel || [];
      for (let i = 0; i < missing.length; i++) {
        const m = missing[i];
        allRows.push({
          keka_emp_number: m.employee_code,
          keka_emp_name: '',
          days_absent: 0,
          matched: false,
          paytrack_name: m.name,
          paytrack_emp_id: null,
          organization: m.organization || '',
          reject_reason: 'Missing from Excel',
          status: 'Missing from Excel',
        });
      }
      const response = await api.post('/salary-records/keka-apply', {
        month: parseInt(selectedMonth),
        year: parseInt(selectedYear),
        employees: matchedEmployees,
        file_name: previewData.file_name,
        all_rows: allRows,
      });
      setPreviewData(prev => ({ ...prev, applyResult: response.data }));
      setStep(6);
      toast.success('LOP Days updated for ' + (response.data.updated + response.data.created) + ' employees');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply LOP Days');
    } finally {
      setIsApplying(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setSelectedMonth('');
    setFileName('');
    setPreviewData(null);
    setActiveFilter('all');
    setUploadedFile(null);
    setExcelHeaders([]);
    setColEmpNo('');
    setColName('');
    setColAbsent('');
  };

  const matchedCount = previewData ? previewData.matched_count : 0;
  const unmatchedCount = previewData ? previewData.unmatched_count : 0;
  const absentCount = previewData ? previewData.absent_count : 0;
  const monthLabel = selectedMonth ? MONTHS[parseInt(selectedMonth) - 1] : '';

  const missingList = previewData ? (previewData.missing_from_excel || []) : [];
  const missingRows = [];
  for (let i = 0; i < missingList.length; i++) {
    const m = missingList[i];
    missingRows.push({ keka_emp_number: m.employee_code, paytrack_name: m.name, organization: m.organization, missing: true });
  }
  const missingCount = missingRows.length;

  const finalizedRows = [];
  if (previewData) {
    for (let i = 0; i < previewData.preview.length; i++) {
      const r = previewData.preview[i];
      if (r.matched && r.finalized) finalizedRows.push(r);
    }
  }
  const finalizedCount = finalizedRows.length;
  const applyCount = Math.max(matchedCount - finalizedCount, 0);

  const excelFiltered = previewData ? previewData.preview.filter(r => {
    if (activeFilter === 'matched') return r.matched;
    if (activeFilter === 'absent') return r.matched && r.days_absent > 0;
    if (activeFilter === 'unmatched') return !r.matched;
    if (activeFilter === 'finalized') return r.matched && r.finalized;
    return true;
  }) : [];
  const filteredPreview = activeFilter === 'missing' ? missingRows
    : activeFilter === 'all' ? excelFiltered.concat(missingRows)
    : excelFiltered;

  const handleCardClick = (filter) => {
    setActiveFilter(prev => prev === filter ? 'all' : filter);
  };

  return (
    <DashboardLayout>
      <div data-testid="keka-upload-page" className="space-y-6 max-w-5xl">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Keka Excel Upload</h1>
          <p className="text-muted-foreground">Upload attendance data from Keka HR to auto-fill LOP Days for salary processing</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b">
          <button
            onClick={() => setActiveTab('upload')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'upload' ? 'border-slate-900 text-slate-900 dark:border-white dark:text-white' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid="tab-upload"
          >
            <Upload className="h-4 w-4 inline mr-1.5 -mt-0.5" />Upload
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'history' ? 'border-slate-900 text-slate-900 dark:border-white dark:text-white' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid="tab-history"
          >
            <History className="h-4 w-4 inline mr-1.5 -mt-0.5" />Upload History
          </button>
        </div>

        {activeTab === 'history' && <UploadHistory />}

        {activeTab === 'upload' && (<>
        <StepIndicator currentStep={step} />

        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileSelected} data-testid="file-input" />

        {step === 1 && (
          <Card>
            <CardHeader><CardTitle className="text-lg">Which month's salary are you processing?</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">The LOP Days values from the Keka Excel will be applied to salary records for this month.</p>
              <div className="flex items-end gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Month</label>
                  <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                    <SelectTrigger className="w-48" data-testid="month-select"><SelectValue placeholder="Select month" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">January</SelectItem>
                      <SelectItem value="2">February</SelectItem>
                      <SelectItem value="3">March</SelectItem>
                      <SelectItem value="4">April</SelectItem>
                      <SelectItem value="5">May</SelectItem>
                      <SelectItem value="6">June</SelectItem>
                      <SelectItem value="7">July</SelectItem>
                      <SelectItem value="8">August</SelectItem>
                      <SelectItem value="9">September</SelectItem>
                      <SelectItem value="10">October</SelectItem>
                      <SelectItem value="11">November</SelectItem>
                      <SelectItem value="12">December</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Year</label>
                  <Select value={selectedYear} onValueChange={setSelectedYear}>
                    <SelectTrigger className="w-32" data-testid="year-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2025">2025</SelectItem>
                      <SelectItem value="2026">2026</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => setStep(2)} disabled={!selectedMonth} data-testid="proceed-to-upload">
                  Next: Upload Excel<ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card>
            <CardHeader><CardTitle className="text-lg">Upload Keka Attendance Excel for {monthLabel} {selectedYear}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Upload the attendance/leave report exported from Keka HR. The system auto-detects the LOP Days column
                (looks for <strong>"Loss Of Pay"</strong>, <strong>"LOP"</strong>, <strong>"LOP Days"</strong>, etc.)
                and matches employees by <strong>Employee Number</strong>.
              </p>
              <div className="border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:border-slate-400 hover:bg-muted/30 transition-all" onClick={() => fileInputRef.current && fileInputRef.current.click()} data-testid="file-drop-zone">
                <FileSpreadsheet className="h-12 w-12 mx-auto text-emerald-600 mb-4" />
                <p className="font-medium text-lg">Click to upload or drag & drop</p>
                <p className="text-sm text-muted-foreground mt-1">.xlsx or .xls files supported</p>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && !headerLoading && excelHeaders.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-lg">Map Columns from "{fileName}"</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Select which Excel columns contain the Employee ID, Name, and LOP Days data.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Employee ID Column *</Label>
                  <Select value={colEmpNo} onValueChange={setColEmpNo}>
                    <SelectTrigger data-testid="col-empno-select"><SelectValue placeholder="Select column" /></SelectTrigger>
                    <SelectContent>
                      {excelHeaders.map((h, i) => (
                        <SelectItem key={i} value={String(i)}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Employee Name Column</Label>
                  <Select value={colName} onValueChange={setColName}>
                    <SelectTrigger data-testid="col-name-select"><SelectValue placeholder="Select column" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- None --</SelectItem>
                      {excelHeaders.map((h, i) => (
                        <SelectItem key={i} value={String(i)}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">LOP Days Column *</Label>
                  <Select value={colAbsent} onValueChange={setColAbsent}>
                    <SelectTrigger data-testid="col-absent-select"><SelectValue placeholder="Select column" /></SelectTrigger>
                    <SelectContent>
                      {excelHeaders.map((h, i) => (
                        <SelectItem key={i} value={String(i)}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {colEmpNo && colAbsent && (
                <div className="p-3 rounded-md bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 text-sm">
                  <strong>Mapping:</strong> "{excelHeaders[parseInt(colEmpNo)]}" → Employee ID, 
                  {colName && colName !== '__none__' ? ` "${excelHeaders[parseInt(colName)]}" → Name, ` : ' '}
                  "{excelHeaders[parseInt(colAbsent)]}" → LOP Days
                </div>
              )}
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                <Button onClick={handleColumnMappingConfirm} disabled={!colEmpNo || !colAbsent} data-testid="confirm-mapping-btn">
                  Confirm & Process <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && headerLoading && (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="animate-spin h-8 w-8 border-2 border-slate-900 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="font-medium">Reading columns from "{fileName}"...</p>
            </CardContent>
          </Card>
        )}

        {step === 4 && isUploading && (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="animate-spin h-8 w-8 border-2 border-slate-900 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="font-medium">Processing "{fileName}"...</p>
              <p className="text-sm text-muted-foreground mt-1">Detecting columns and matching employees</p>
            </CardContent>
          </Card>
        )}

        {step === 5 && previewData && (
          <div className="space-y-4">
            <Card className="border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20 dark:border-emerald-800">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    <p className="font-medium text-emerald-900 dark:text-emerald-300">
                      Column Auto-Detected: "{previewData.detected_absent_column}" mapped to "Unpaid Leave Taken"
                    </p>
                    <p className="text-sm text-emerald-700 dark:text-emerald-400">
                      File: {previewData.file_name} | Matching by: <strong>{previewData.detected_empno_column}</strong> (Keka) = <strong>Employee ID</strong> (PayTrack)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-5 gap-3">
              <Card className={`cursor-pointer transition-all hover:shadow-md ${activeFilter === 'matched' ? 'ring-2 ring-emerald-500 bg-emerald-50/80 dark:bg-emerald-950/30' : ''}`} onClick={() => handleCardClick('matched')} data-testid="filter-matched">
                <CardContent className="p-4 text-center"><p className="text-2xl font-bold text-emerald-600 font-mono">{matchedCount}</p><p className="text-xs text-muted-foreground mt-1">Employees Matched</p></CardContent>
              </Card>
              <Card className={`cursor-pointer transition-all hover:shadow-md ${activeFilter === 'absent' ? 'ring-2 ring-amber-500 bg-amber-50/80 dark:bg-amber-950/30' : ''}`} onClick={() => handleCardClick('absent')} data-testid="filter-absent">
                <CardContent className="p-4 text-center"><p className="text-2xl font-bold text-amber-600 font-mono">{absentCount}</p><p className="text-xs text-muted-foreground mt-1">With LOP Days &gt; 0</p></CardContent>
              </Card>
              <Card className={`cursor-pointer transition-all hover:shadow-md ${activeFilter === 'finalized' ? 'ring-2 ring-sky-500 bg-sky-50/80 dark:bg-sky-950/30' : ''}`} onClick={() => handleCardClick('finalized')} data-testid="filter-finalized">
                <CardContent className="p-4 text-center"><p className="text-2xl font-bold text-sky-600 font-mono">{finalizedCount}</p><p className="text-xs text-muted-foreground mt-1">Finalized (Locked)</p></CardContent>
              </Card>
              <Card className={`cursor-pointer transition-all hover:shadow-md ${activeFilter === 'unmatched' ? 'ring-2 ring-red-500 bg-red-50/80 dark:bg-red-950/30' : ''}`} onClick={() => handleCardClick('unmatched')} data-testid="filter-unmatched">
                <CardContent className="p-4 text-center"><p className="text-2xl font-bold text-red-600 font-mono">{unmatchedCount}</p><p className="text-xs text-muted-foreground mt-1">Not Found in PayTrack</p></CardContent>
              </Card>
              <Card className={`cursor-pointer transition-all hover:shadow-md ${activeFilter === 'missing' ? 'ring-2 ring-orange-500 bg-orange-50/80 dark:bg-orange-950/30' : ''}`} onClick={() => handleCardClick('missing')} data-testid="filter-missing">
                <CardContent className="p-4 text-center"><p className="text-2xl font-bold text-orange-600 font-mono">{missingCount}</p><p className="text-xs text-muted-foreground mt-1">Missing from Excel</p></CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Preview: LOP Days Update for {monthLabel} {selectedYear}</CardTitle>
                  {activeFilter !== 'all' && (
                    <Badge variant="outline" className="text-xs cursor-pointer hover:bg-muted" onClick={() => setActiveFilter('all')}>
                      Showing: {activeFilter === 'matched' ? 'Matched' : activeFilter === 'absent' ? 'LOP Days > 0' : activeFilter === 'missing' ? 'Missing from Excel' : activeFilter === 'finalized' ? 'Finalized (Locked)' : 'Not Matched'} ({filteredPreview.length}) — click to clear
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Emp No. (Keka)</TableHead>
                        <TableHead>Name (Keka)</TableHead>
                        <TableHead>Name (PayTrack)</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead className="text-center">LOP Days</TableHead>
                        <TableHead className="text-center">Match Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <PreviewTable rows={filteredPreview} />
                    </TableBody>
                  </Table>
                </div>

                {unmatchedCount > 0 && (
                  <div className="flex items-start gap-2 mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                      <strong>{unmatchedCount} employee(s)</strong> from the Keka file could not be matched. These will be skipped.
                    </p>
                  </div>
                )}

                {finalizedCount > 0 && (
                  <div className="flex items-start gap-2 mt-3 p-3 rounded-lg bg-sky-50 dark:bg-sky-950/20 border border-sky-200 dark:border-sky-800" data-testid="finalized-warning-banner">
                    <Lock className="h-4 w-4 text-sky-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-sky-800 dark:text-sky-300">
                      <strong>{finalizedCount} matched employee(s)</strong> already have a FINALIZED salary record for {monthLabel} {selectedYear} — their LOP days will NOT be overwritten. Un-finalize them in Salary Entry first if this upload should update them.
                    </p>
                  </div>
                )}

                {missingCount > 0 && (
                  <div className="flex items-start gap-2 mt-3 p-3 rounded-lg bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800" data-testid="missing-warning-banner">
                    <UserX className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-orange-800 dark:text-orange-300">
                      <strong>{missingCount} active employee(s)</strong> in PayTrack are NOT present in this Keka file — their LOP days will NOT be updated for {monthLabel} {selectedYear}. Click the "Missing from Excel" card above to see who.
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t">
                  <Button variant="outline" onClick={handleReset} data-testid="cancel-upload"><RotateCcw className="h-4 w-4 mr-2" />Start Over</Button>
                  <Button onClick={handleConfirmApply} disabled={isApplying || matchedCount === 0} className="min-w-[200px]" data-testid="confirm-update-btn">
                    {isApplying ? (<><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />Updating...</>) : (<><CheckCircle2 className="h-4 w-4 mr-2" />Confirm & Update {applyCount} Employees</>)}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <AlertDialogContent data-testid="missing-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-600" />
                    Review before applying
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {missingCount > 0 ? `${missingCount} active employee(s) are missing from "${fileName}" — their LOP days will NOT be updated. ` : ''}
                    {finalizedCount > 0 ? `${finalizedCount} matched employee(s) already have a finalized salary record for ${monthLabel} ${selectedYear} — they will be skipped, not overwritten. ` : ''}
                    Proceed anyway?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="max-h-56 overflow-y-auto space-y-2">
                  {finalizedCount > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-sky-700 flex items-center gap-1"><Lock className="h-3 w-3" />Finalized — will be skipped ({finalizedCount})</p>
                      <MissingNamesList rows={finalizedRows} />
                    </div>
                  )}
                  {missingCount > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-orange-700 flex items-center gap-1"><UserX className="h-3 w-3" />Missing from Excel — LOP not updated ({missingCount})</p>
                      <MissingNamesList rows={missingRows} />
                    </div>
                  )}
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="missing-cancel-btn">Go Back &amp; Review</AlertDialogCancel>
                  <AlertDialogAction onClick={doApply} data-testid="missing-proceed-btn">Proceed Anyway</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}

        {step === 6 && (
          <Card className="border-emerald-200">
            <CardContent className="p-12 text-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold">LOP Days Updated Successfully</h2>
                <p className="text-muted-foreground mt-1">Records processed for {monthLabel} {selectedYear}</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 max-w-md mx-auto text-sm text-left space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Created:</span><span className="font-mono font-medium">{previewData && previewData.applyResult ? previewData.applyResult.created : 0}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Updated:</span><span className="font-mono font-medium">{previewData && previewData.applyResult ? previewData.applyResult.updated : 0}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Skipped (finalized):</span><span className="font-mono font-medium text-amber-600">{previewData && previewData.applyResult ? previewData.applyResult.skipped_finalized : 0}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Not matched:</span><span className="font-mono font-medium text-red-600">{unmatchedCount}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Missing from Excel:</span><span className="font-mono font-medium text-orange-600">{missingCount}</span></div>
              </div>
              <div className="flex items-center justify-center gap-3 pt-2">
                <Button variant="outline" onClick={handleReset} data-testid="upload-another"><Upload className="h-4 w-4 mr-2" />Upload Another</Button>
                <Button onClick={() => window.location.href = '/salary-reports'} data-testid="go-to-salary">Go to Salary Entry<ArrowRight className="h-4 w-4 ml-2" /></Button>
              </div>
            </CardContent>
          </Card>
        )}
        </>)}
      </div>
    </DashboardLayout>
  );
}
